import React, { Component } from 'react';
import { connect } from 'react-redux';
import WizardStepBar from './WizardStepBar';
import WizardNavigation from './WizardNavigation';
import StepSelectProduct from './steps/StepSelectProduct';
import StepConfigure from './steps/StepConfigure';
import StepReview from './steps/StepReview';
import StepTakeAction from './steps/StepTakeAction';
import StepQuote from './steps/StepQuote';
import WizardSidebar from './WizardSidebar';
import Bom from '../bom';
import { getActiveProject, modalProgressShowing, getUpdateParameters, getParameters } from '../../reducers/mainReducer';
import './Wizard.css';

const STEPS = [
    { label: 'Select Product', key: 'select' },
    { label: 'Configure', key: 'configure' },
    { label: 'Review', key: 'review' },
    { label: 'Downloads', key: 'downloads' },
    { label: 'Quote', key: 'quote' }
];

const WIZARD_STATE_KEY = 'wizardState';

function loadWizardState() {
    try {
        const saved = sessionStorage.getItem(WIZARD_STATE_KEY);
        if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return null;
}

function saveWizardState(state) {
    try {
        sessionStorage.setItem(WIZARD_STATE_KEY, JSON.stringify({
            currentStep: state.currentStep,
            highWaterStep: state.highWaterStep,
            prevProjectId: state.prevProjectId
        }));
    } catch { /* ignore */ }
}

export function clearWizardState() {
    sessionStorage.removeItem(WIZARD_STATE_KEY);
}

export class Wizard extends Component {
    constructor(props) {
        super(props);
        const saved = loadWizardState();
        this.state = {
            currentStep: saved?.currentStep ?? 0,
            highWaterStep: saved?.highWaterStep ?? 0,
            prevProjectId: saved?.prevProjectId ?? null,
            showBom: false
        };
        this.handleNext = this.handleNext.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.handleStepClick = this.handleStepClick.bind(this);
        this.handleProductSelected = this.handleProductSelected.bind(this);
        this.handleStartOver = this.handleStartOver.bind(this);
        this.confirmLeaveDownloads = this.confirmLeaveDownloads.bind(this);
    }

    static getDerivedStateFromProps(props, state) {
        const projectId = props.activeProject?.id;
        if (projectId && state.prevProjectId && projectId !== state.prevProjectId) {
            const newState = {
                prevProjectId: projectId,
                currentStep: 1,
                highWaterStep: 1,
                showBom: false
            };
            saveWizardState(newState);
            return newState;
        }
        if (projectId !== state.prevProjectId) {
            return { prevProjectId: projectId };
        }
        return null;
    }

    componentDidUpdate(_, prevState) {
        if (prevState.currentStep !== this.state.currentStep ||
            prevState.highWaterStep !== this.state.highWaterStep ||
            prevState.prevProjectId !== this.state.prevProjectId) {
            saveWizardState(this.state);
        }
    }

    canProceed() {
        const { currentStep } = this.state;
        const { activeProject, modalProgressShowing } = this.props;

        switch (currentStep) {
            case 0:
                return !!(activeProject && activeProject.id);
            case 1:
                return !!(
                    activeProject &&
                    activeProject.svf &&
                    activeProject.hash &&
                    !modalProgressShowing
                );
            case 2:
                return true;
            case 3:
                return true; // Downloads — always allow proceeding to Quote
            case 4:
                return false; // Quote — last step
            default:
                return false;
        }
    }

    handleProductSelected() {
        const next = 1;
        this.setState({
            currentStep: next,
            highWaterStep: Math.max(this.state.highWaterStep, next)
        });
    }

    handleStepComplete(completedStep) {
        // When Configure (step 1) completes and moves to Review (step 2), unlock all steps
        if (completedStep >= 1) {
            this.setState({ highWaterStep: STEPS.length - 1 });
        }
    }

    handleNext() {
        if (this.canProceed() && this.state.currentStep < STEPS.length - 1) {
            const next = this.state.currentStep + 1;
            // Once you reach Review (step 2), unlock all remaining steps
            const newHighWater = next >= 2 ? STEPS.length - 1 : Math.max(this.state.highWaterStep, next);
            this.setState({
                currentStep: next,
                highWaterStep: newHighWater
            });
        }
    }

    confirmLeaveDownloads() {
        // Warn when navigating backwards from Downloads step (index 3) or Quote step (index 4)
        if (this.state.currentStep < 3) return true;
        return window.confirm(
            "If you haven't downloaded your files, they won't be available unless you reconfigure this product. Do you want to continue?"
        );
    }

    handleBack() {
        if (this.state.currentStep > 0) {
            if (!this.confirmLeaveDownloads()) return;
            this.setState({ currentStep: this.state.currentStep - 1 });
        }
    }

    handleStepClick(stepIndex) {
        if (stepIndex <= this.state.highWaterStep) {
            if (stepIndex < this.state.currentStep && !this.confirmLeaveDownloads()) return;
            this.setState({ currentStep: stepIndex });
        }
    }

    handleStartOver() {
        if (!this.confirmLeaveDownloads()) return;
        clearWizardState();
        this.setState({
            currentStep: 0,
            highWaterStep: 0,
            prevProjectId: null,
            showBom: false,
            showConfigSummary: false
        });
    }

    renderStepContent() {
        switch (this.state.currentStep) {
            case 0:
                return <StepSelectProduct onProductSelected={this.handleProductSelected} />;
            case 1:
                return <StepConfigure onCartUpdated={this.props.onCartUpdated} onUpdateComplete={() => this.handleNext()} />;
            case 2:
                return (
                    <StepReview
                        showDrawing={!!this.state.showDrawing}
                        onCloseDrawing={() => this.setState({ showDrawing: false })}
                    />
                );
            case 3:
                return <StepTakeAction />;
            case 4:
                return (
                    <StepQuote
                        onCartUpdated={this.props.onCartUpdated}
                        onStartOver={this.handleStartOver}
                        isAnonymous={this.props.isAnonymous}
                        hideQuoteButtons={this.props.hideQuoteButtons}
                    />
                );
            default:
                return null;
        }
    }

    render() {
        const { currentStep, highWaterStep, showBom } = this.state;
        const { activeProject, projectUpdateParameters, projectSourceParameters } = this.props;
        const completedSteps = new Set();
        for (let i = 0; i < highWaterStep; i++) {
            completedSteps.add(i);
        }

        const isLastStep = currentStep === STEPS.length - 1;
        const modelGenerated = !!(activeProject && activeProject.svf && activeProject.hash);
        const showSidebar = currentStep >= 2 && currentStep < 4; // Review, Downloads (not Quote — it has its own summary)

        return (
            <div className="wizard-container">
                <WizardStepBar
                    steps={STEPS}
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    onStepClick={this.handleStepClick}
                />
                <div className="wizard-body">
                    {showSidebar && (
                        <WizardSidebar
                            activeProject={activeProject}
                            projectUpdateParameters={projectUpdateParameters}
                            projectSourceParameters={projectSourceParameters}
                            modelGenerated={modelGenerated}
                            currentStep={currentStep}
                            onViewBom={() => this.setState({ showBom: true })}
                            onViewDrawing={() => this.setState({ showDrawing: true })}
                        />
                    )}
                    <div className="wizard-content">
                        <div className="wizard-content-inner">
                            {this.renderStepContent()}
                        </div>
                        <WizardNavigation
                            currentStep={currentStep}
                            canGoNext={this.canProceed()}
                            canGoBack={currentStep > 0}
                            onNext={this.handleNext}
                            onBack={this.handleBack}
                            isLastStep={isLastStep}
                        />
                    </div>
                </div>

                {/* BOM Popup Modal */}
                {showBom && (
                    <div className="wizard-modal-overlay" onClick={() => this.setState({ showBom: false })}>
                        <div className="wizard-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="wizard-modal-header">
                                <h3>Bill of Materials</h3>
                                <button
                                    className="wizard-modal-close"
                                    onClick={() => this.setState({ showBom: false })}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="wizard-modal-body">
                                <Bom />
                            </div>
                        </div>
                    </div>
                )}

            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function(store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject,
        modalProgressShowing: modalProgressShowing(store),
        projectUpdateParameters: getUpdateParameters(activeProject.id, store),
        projectSourceParameters: getParameters(activeProject.id, store)
    };
})(Wizard);
